/**
 * OneSignal Web Push — public API + SDK lifecycle (web only).
 *
 * Delivery is server-driven: the backend sends pushes via the OneSignal REST API
 * on events (new order, service request, chat message), each carrying a
 * `web_url` deep-link. The frontend only:
 *   - initializes the Web SDK,
 *   - asks for permission (friendly soft slidedown),
 *   - identifies the user (external id `staff_<id>`) so the backend can target,
 *   - suppresses the browser push while the app is focused (foregroundWillDisplay).
 *
 * Click navigation is driven entirely by the backend `web_url`. The only local
 * glue is the chat deep-link (`?openChat=`), captured in pending-deeplink.ts —
 * the chat panel isn't a route, so a plain URL can't open it.
 *
 * Concerns are split across sibling files:
 *   - onesignal.config.ts       — init options + slidedown i18n
 *   - notification-handlers.ts  — foregroundWillDisplay glue
 *   - pending-deeplink.ts       — cold chat `?openChat=` capture/consume
 *
 * No-op in Electron. Every call is guarded/caught so a failure never breaks the
 * app; in-app notifications keep working regardless.
 */
import OneSignal from 'react-onesignal';
import { getOneSignalAppId } from '@shared/config/runtime-config';
import { isElectronApp } from '@hooks/use-is-electron';
import { getOneSignalInitOptions } from './onesignal.config.ts';
import { handleForegroundWillDisplay } from './notification-handlers.ts';

const LOG_PREFIX = '[OneSignal]';

let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Initializes OneSignal once per page load. Safe to call repeatedly — the
 * promise is kept even on failure so `init` is never called twice ("SDK already
 * initialized"). No-op in Electron and when no app id is configured.
 */
export function initOneSignal(): Promise<void> {
  if (isElectronApp()) return Promise.resolve();
  if (initPromise) return initPromise;

  const appId = getOneSignalAppId();
  if (!appId) {
    console.warn(`${LOG_PREFIX} app id not configured, skipping init`);
    return Promise.resolve();
  }

  initPromise = (async () => {
    try {
      await OneSignal.init(getOneSignalInitOptions(appId));

      OneSignal.Notifications.addEventListener(
        'foregroundWillDisplay',
        handleForegroundWillDisplay
      );

      initialized = true;
    } catch (error) {
      console.error(`${LOG_PREFIX} init failed`, error);
    }
  })();

  return initPromise;
}

/**
 * Links the current user to their OneSignal subscription. The backend targets
 * pushes by external id, agreed format `staff_<userId>`.
 */
export async function identifyUser(userId: number | string): Promise<void> {
  if (isElectronApp()) return;
  await initOneSignal(); // safe to call again — returns the same cached promise
  if (!initialized) return; // init failed for another reason

  try {
    await OneSignal.login(`staff_${userId}`);
    await OneSignal.User.PushSubscription.optIn();
  } catch (error) {
    console.error(`${LOG_PREFIX} identifyUser failed`, error);
  }
}

/**
 * Shows the friendly soft-prompt (slidedown) asking for push permission. Does
 * nothing if permission was already granted/denied by the browser.
 */
export async function promptForPermission(): Promise<void> {
  if (isElectronApp() || !initialized) return;

  try {
    if (OneSignal.Notifications.permission) return;
    await OneSignal.Slidedown.promptPush();
  } catch (error) {
    console.error(`${LOG_PREFIX} promptForPermission failed`, error);
  }
}

/** Unlinks the current user (call on logout). */
export async function logoutOneSignal(): Promise<void> {
  if (isElectronApp() || !initialized) return;

  try {
    await OneSignal.logout();
  } catch (error) {
    console.error(`${LOG_PREFIX} logout failed`, error);
  }
}
